import { useCallback, useEffect, useRef } from "react";
import * as THREE from "three";
import { EDraggablePointType } from "../@types/Enums";
import globalEventManager from "../utils/globalEventManager";
import { useDraggingContext } from "../provider/DraggingProvider";

const useMeasurementDragging = (
  raycaster: THREE.Raycaster,
  camera: THREE.Camera,
  scene: THREE.Scene, 
  setStartPoint: (point: THREE.Vector3) => void,
  setEndPoint: (point: THREE.Vector3) => void
) => {
  const draggingPointTypeRef = useRef<EDraggablePointType | null>(null);
  const { setIsDragging } = useDraggingContext();

  const handlePointerMove = useCallback(
    (event: PointerEvent) => {
      const mouse = new THREE.Vector2(
        (event.clientX / window.innerWidth) * 2 - 1,
        -(event.clientY / window.innerHeight) * 2 + 1
      );
      raycaster.setFromCamera(mouse, camera);

      // Intersect with scene children directly
      const intersects = raycaster.intersectObjects(scene.children, true);

      if (intersects.length === 0) return;

      const newPos = intersects[0].point.clone();
      newPos.z = 0;

      if (draggingPointTypeRef.current === EDraggablePointType.START) {
        setStartPoint(newPos);
      } else if (draggingPointTypeRef.current  === EDraggablePointType.END) {
        setEndPoint(newPos);
      }
    },
    [camera, raycaster, scene, setEndPoint, setStartPoint]
  );

  const handleGlobalPointerUp = useCallback(() => {
    draggingPointTypeRef.current = null;
    setIsDragging(false);
  }, [setIsDragging]);

  useEffect(() => {
    if (draggingPointTypeRef.current !== null) {
      setIsDragging(true);
      globalEventManager.registerPointerMove(handlePointerMove);
      globalEventManager.registerPointerUp(handleGlobalPointerUp);
    }

    return () => {
      globalEventManager.unregisterPointerMove(handlePointerMove);
      globalEventManager.unregisterPointerUp(handleGlobalPointerUp);
    };
  }, [handleGlobalPointerUp, handlePointerMove, setIsDragging]);

  const setDraggingPointType = (pointType: EDraggablePointType | null) => {
    draggingPointTypeRef.current = pointType;
  };

  return {
    setDraggingPointType,
  };
};

export default useMeasurementDragging;
